import React, { useState } from 'react';
import { doc, runTransaction, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CalendarCheck, Clock, X, AlertCircle } from 'lucide-react';

const ZONE = 'America/Chicago';

// All jobs in this phase are Wisconsin appointments; storing local date/time plus
// an IANA zone prevents dates from shifting when a client views them abroad.
const wisconsinNow = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

export default function WorkOrderScheduleModal({ job, contractors, adminId, onClose }) {
  const existing = job.schedule || {};
  const today = wisconsinNow().date;
  const [date, setDate] = useState(existing.date || today);
  const [startTime, setStartTime] = useState(existing.startTime || '09:00');
  const [endTime, setEndTime] = useState(existing.endTime || '11:00');
  const [notes, setNotes] = useState(existing.notes || '');
  const [contractorId, setContractorId] = useState(job.contractorId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const scheduleEligible = job.depositPaid === true && ['deposit_paid', 'scheduled'].includes(job.status);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    if (!scheduleEligible) { setError('A verified deposit is required before scheduling.'); return; }
    if (!adminId) { setError('An authenticated administrator is required.'); return; }
    const now = wisconsinNow();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(`${date}T12:00:00Z`))) {
      setError('Choose a valid service date.'); return;
    }
    if (date < now.date || (date === now.date && startTime <= now.time)) {
      setError('The appointment must start in the future (Central Time).'); return;
    }
    if (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime) || endTime <= startTime) {
      setError('The ending time must be later than the starting time on the same date.'); return;
    }
    if (notes.length > 1000) { setError('Notes must be 1,000 characters or fewer.'); return; }
    const selectedContractor = contractors.find(c => c.id === contractorId);
    if (contractorId && !selectedContractor) { setError('Select a valid technician.'); return; }

    setSaving(true);
    try {
      const jobRef = doc(db, 'workOrders', job.id);
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(jobRef);
        if (!snapshot.exists()) throw new Error('Work order no longer exists.');
        const current = snapshot.data();
        if (current.depositPaid !== true || !['deposit_paid', 'scheduled'].includes(current.status)) {
          throw new Error('This job is no longer eligible for scheduling. Refresh the page.');
        }
        if (current.quote?.status !== 'accepted') {
          throw new Error('The customer must accept the quote before scheduling.');
        }
        transaction.update(jobRef, {
          status: 'scheduled',
          schedule: {
            date, startTime, endTime, timeZone: ZONE,
            notes: notes.trim(),
            revision: (Number(current.schedule?.revision) || 0) + 1,
            confirmedBy: adminId,
            confirmedAt: serverTimestamp()
          },
          contractorId: selectedContractor?.id || deleteField(),
          contractorName: selectedContractor?.name || deleteField(),
          contractorEmail: selectedContractor?.email || deleteField(),
          visibleToContractors: false,
          assignedAt: selectedContractor ? serverTimestamp() : deleteField(),
          updatedAt: serverTimestamp()
        });
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not confirm appointment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="schedule-title">
      <section className="bg-white max-w-xl w-full rounded-xl shadow-xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 border-b pb-4 mb-5">
          <div>
            <h2 id="schedule-title" className="text-xl font-bold text-slate-900 flex items-center gap-2"><CalendarCheck className="text-blue-700" /> {job.schedule ? 'Reschedule appointment' : 'Confirm appointment'}</h2>
            <p className="text-sm text-slate-600 mt-1">{job.serviceType} · {job.clientName || job.clientEmail || job.id}</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="text-gray-500 hover:text-gray-900"><X /></button>
        </div>

        {!scheduleEligible && <div role="alert" className="flex gap-2 bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded mb-4"><AlertCircle size={20}/> A verified deposit is required before confirming this appointment.</div>}
        <p className="text-sm text-gray-600 mb-5">Appointment times are in <strong>Central Time (Wisconsin)</strong>. The customer's preferred date is a request, not an existing confirmed appointment.</p>

        <form onSubmit={save} className="space-y-4">
          <div>
            <label htmlFor="schedule-date" className="block text-sm font-semibold mb-1">Confirmed service date *</label>
            <input id="schedule-date" required type="date" min={today} value={date} onChange={e => setDate(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label htmlFor="schedule-start" className="block text-sm font-semibold mb-1">Start (CT) *</label><input id="schedule-start" required type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3"/></div>
            <div><label htmlFor="schedule-end" className="block text-sm font-semibold mb-1">End (CT) *</label><input id="schedule-end" required type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3"/></div>
          </div>
          <div>
            <label htmlFor="schedule-contractor" className="block text-sm font-semibold mb-1">Assigned technician (optional)</label>
            <select id="schedule-contractor" value={contractorId} onChange={e => setContractorId(e.target.value)} className="w-full rounded-lg border border-gray-300 p-3">
              <option value="">Chuko Logic — assignment to be confirmed</option>
              {contractors.map(c => <option key={c.id} value={c.id}>{c.name || c.email || c.id}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">The appointment can be confirmed before a technician account is assigned.</p>
          </div>
          <div>
            <label htmlFor="schedule-notes" className="block text-sm font-semibold mb-1">Arrival or access instructions (visible to client)</label>
            <textarea id="schedule-notes" rows={3} maxLength={1000} value={notes} onChange={e => setNotes(e.target.value)} placeholder="E.g., ask for site manager on arrival" className="w-full rounded-lg border border-gray-300 p-3"/>
          </div>
          <p className="text-sm text-blue-900 bg-blue-50 rounded p-3 flex items-start gap-2"><Clock size={18} className="shrink-0"/> Saving immediately confirms the appointment in the client portal. Check technician availability before submitting. No additional payment will be collected.</p>
          {error && <p role="alert" className="text-sm text-red-800 bg-red-50 border border-red-200 p-3 rounded">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" disabled={saving} onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
            <button type="submit" disabled={saving || !scheduleEligible} className="px-5 py-2 bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50">{saving ? 'Saving…' : job.schedule ? 'Confirm new time' : 'Confirm schedule'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
